import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminSponsoredPricing, adminSponsoredRatecard } from "../lib/api";

const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function SponsoredRateCard({ onSaved }) {
  const [d, setD] = useState(null);
  const [edits, setEdits] = useState({});
  const [cpc, setCpc] = useState("");
  const [showAll, setShowAll] = useState(false);
  const load = () =>
    adminSponsoredRatecard()
      .then((x) => { setD(x); setEdits({}); setCpc(String(x.cpc_usd ?? 1)); })
      .catch(() => toast.error("could not load rate card"));
  useEffect(() => {
    Promise.resolve().then(load);
  }, []);
  if (!d) return null;

  const cpcNum = Number(cpc) || 0;
  const suggestFor = (p) => Math.round((p.clicks_30d || 0) * cpcNum);

  const save = async () => {
    const overrides = {};
    d.pages.forEach((p) => { if (p.override) overrides[p.placement] = p.price_usd; });
    Object.entries(edits).forEach(([k, v]) => {
      const n = Number(v);
      const tp = d.tiers[(d.pages.find((p) => p.placement === k) || {}).tier];
      if (v === "" || !n || n === tp) delete overrides[k];
      else overrides[k] = n;
    });
    try {
      await adminSponsoredPricing({ overrides, cpc_usd: cpcNum > 0 ? cpcNum : undefined });
      toast.success("per-page prices saved");
      load();
      onSaved?.();
    } catch {
      toast.error("failed");
    }
  };

  // Fill the override inputs for every visible page that gets clicks, using the live CPC.
  const useAllSuggested = () => {
    setEdits((x) => {
      const next = { ...x };
      rows.forEach((p) => { if (p.clicks_30d > 0) next[p.placement] = String(suggestFor(p)); });
      return next;
    });
  };

  const rows = showAll ? d.pages : d.pages.slice(0, 25);
  const cpcDirty = cpcNum > 0 && cpcNum !== (d.cpc_usd ?? 1);
  const dirty = Object.keys(edits).length > 0 || cpcDirty;
  const projected = rows.reduce((sum, p) => sum + p.cap * suggestFor(p), 0);
  return (
    <div className="mt-4" data-testid="sponsored-ratecard">
      <div className="font-bold">rate card — monthly slot price per page, priced on outbound clicks</div>
      <p className="text-[#666] mt-1">
        Outbound clicks are the money metric: a sponsor pays for the qualified referral traffic a page sends them. Over the last {d.view_days} days this index saw{" "}
        <b className="text-black">{(d.site_clicks_30d ?? 0).toLocaleString()} outbound clicks</b> and {(d.site_views_30d ?? 0).toLocaleString()} page views.
        The <b className="text-black">suggested</b> price = a page&apos;s clicks × your cost-per-click. Leave a price blank to use the tier price, or type a number to override that page.
        Max revenue at current prices: <b className="text-black" data-testid="ratecard-max-revenue">{usd(d.inventory.max_month_usd)}/month · {usd(d.inventory.max_annual_usd)}/year</b> across {d.inventory.slots} slots.
      </p>
      <div className="flex flex-wrap gap-3 mt-2 items-center text-[13px]">
        <label className="flex items-center gap-2">
          <span className="text-[#666]">cost per outbound click</span>
          <span>$</span>
          <input value={cpc} onChange={(e) => setCpc(e.target.value)} inputMode="decimal"
            className="border border-black p-1 w-20 text-[12px] font-mono" data-testid="ratecard-cpc" />
        </label>
        <button type="button" className="tv-link underline" onClick={useAllSuggested} data-testid="ratecard-use-all-suggested">[use suggested on every page below]</button>
        <span className="text-[#888]" data-testid="ratecard-projected">
          ceiling = {usd(projected)}/month if every slot on the {rows.length} pages shown sells
        </span>
      </div>
      <div className="overflow-x-auto mt-2">
        <table className="text-left" data-testid="ratecard-table">
          <thead><tr className="border-b border-black text-[11px] text-[#666]"><th className="pr-3">page</th><th className="pr-3">tier</th><th className="pr-3 text-right">clicks/{d.view_days}d</th><th className="pr-3 text-right">views/{d.view_days}d</th><th className="pr-3">slots</th><th className="pr-3 text-right">suggested</th><th className="pr-3">$/month</th><th>per click</th></tr></thead>
          <tbody>
            {rows.map((p) => {
              const tierPrice = d.tiers[p.tier];
              const val = edits[p.placement] !== undefined ? edits[p.placement] : (p.override ? String(p.price_usd) : "");
              const eff = val === "" ? tierPrice : Number(val) || tierPrice;
              const perClick = p.clicks_30d ? eff / p.clicks_30d : null;
              const suggested = suggestFor(p);
              return (
                <tr key={p.placement} className="border-b border-[#eee]" data-testid={`ratecard-row-${p.placement}`}>
                  <td className="pr-3 py-1"><a href={p.path} className="tv-link" target="_blank" rel="noreferrer">{p.path}</a>{p.label && p.kind !== "alternatives" ? <span className="text-[#888]"> {p.label}</span> : null}</td>
                  <td className="pr-3 text-[#666]">{p.tier}</td>
                  <td className="pr-3 text-right font-bold" data-testid={`ratecard-clicks-${p.placement}`}>{p.clicks_30d}</td>
                  <td className="pr-3 text-right text-[#666]">{p.views_30d}</td>
                  <td className="pr-3">{p.slots_taken}/{p.cap}</td>
                  <td className="pr-3 text-right whitespace-nowrap">
                    <span className="text-[#888]" data-testid={`ratecard-suggested-${p.placement}`}>{suggested ? usd(suggested) : "–"}</span>
                    {suggested ? <button type="button" className="tv-link underline ml-2 text-[11px]" onClick={() => setEdits((x) => ({ ...x, [p.placement]: String(suggested) }))} data-testid={`ratecard-use-${p.placement}`}>[use]</button> : null}
                  </td>
                  <td className="pr-3">
                    <input value={val} placeholder={String(tierPrice)} onChange={(e) => setEdits((x) => ({ ...x, [p.placement]: e.target.value }))}
                      className="border border-black p-1 w-20 text-[12px] font-mono" data-testid={`ratecard-price-${p.placement}`} />
                  </td>
                  <td className="text-[#666]">{perClick ? `$${perClick.toFixed(2)}` : "–"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-3 mt-2 items-center">
        <button type="button" className="tv-btn tv-btn-primary" disabled={!dirty} onClick={save} data-testid="ratecard-save">[save prices + cpc]</button>
        {d.pages.length > 25 && <button type="button" className="tv-link underline" onClick={() => setShowAll((s) => !s)} data-testid="ratecard-toggle-all">{showAll ? "show top 25" : `show all ${d.pages.length} pages`}</button>}
      </div>
    </div>
  );
}
